import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import vetuste from './vetuste';
import './sinistrevetuste.scss';

const ALLOWED_KEYS = ['Tab', 'Shift', 'Control', 'Alt', 'Meta', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
const preventDefault = (e) => e.preventDefault();
const blurTarget = (e) => e.target.blur();

const SinistreVetuste = ({
    label,
    value,
    error,
    touched,
    required,
    onBlur,
    name,
    values,
    isValid,
    disabled,
    additional_label,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    placeholder,
    showLabel,
    valeurAchatFieldName,
    dateAchatFieldName,
    dateSurv,
    isMAE = false,
    ...other
}) => {
    const valeurAchat = values.data?.[valeurAchatFieldName]?.new_value || values[valeurAchatFieldName];

    const [vetusteResult, setVetusteResult] = useState(null);
    const [localError, setLocalError] = useState(null);
    const [isLocalTouched, setIsLocalTouched] = useState(false);

    const hasValue = isMAE ? (value !== null && value !== undefined && value !== '') : (value && parseFloat(value) > 0);
    const isFieldTouched = isLocalTouched || touched;
    const displayError = localError || error;
    const canCalculate = valeurAchat && dateSurv && !disabled;
    const hasValeurAchat = valeurAchat && parseFloat(valeurAchat) > 0;
    const displayLabel = isMAE ? 'Montant de la vétusté' : label;

    const setError = useCallback((msg) => {
        setFieldError?.(name, msg || undefined);
        setLocalError(msg);
    }, [setFieldError, name]);

    const validateMontant = useCallback(() => {
        if (!required || hasValue) {
            setError(null);
            return true;
        }
        setError(hasValeurAchat ? 'Veuillez calculer la vétusté' : 'Ce champ est obligatoire');
        return false;
    }, [required, hasValue, hasValeurAchat, setError]);

    useEffect(() => {
        if (isFieldTouched) validateMontant();
    }, [isFieldTouched, validateMontant]);

    useEffect(() => {
        if (required) {
            const inputName = name.match(/data\[([^\]]+)\]/)?.[1];
            if (inputName) {
                setFieldValue?.(`data[${inputName}][_vetusteRequired]`, !hasValue);
            }
        }
    }, [required, hasValue, setFieldValue, name]);

    useEffect(() => {
        setFieldValue(name, null);
    }, [valeurAchat, setFieldValue, name]);

    const markTouched = useCallback(() => {
        setIsLocalTouched(true);
        setFieldTouched?.(name, true);
    }, [setFieldTouched, name]);

    const handleBlur = (e) => {
        if (hasValue) return;
        markTouched();
        setTimeout(validateMontant, 100);
        onBlur?.(e);
    };

    const handleInteraction = () => {
        if (!hasValue) {
            markTouched();
            setTimeout(validateMontant, 50);
        }
    };

    const calculVetuste = (e) => {
        e.preventDefault();
        markTouched();

        const dateAchat = values.data?.[dateAchatFieldName]?.new_value || values[dateAchatFieldName];
        const montant = parseFloat(valeurAchat) || 0;

        if (!montant || !dateAchat || !dateSurv) {
            alert('Veuillez renseigner la valeur d\'achat, la date d\'achat et la date de survenance');
            return;
        }

        const mDateAchat = moment(dateAchat, 'DD/MM/YYYY');
        const mDateSurv = moment(dateSurv, 'YYYY-MM-DD');

        if (!mDateAchat.isValid() || !mDateSurv.isValid()) {
            alert('Format de date invalide');
            return;
        }

        try {
            const result = vetuste(mDateSurv, mDateAchat, montant, isMAE);
            setVetusteResult({ palier: result.palier, explication: result.explication });
            setFieldValue?.(name, isMAE ? result.montantVetuste : result.valeurRemboursement);
            setError(null);
        } catch (err) {
            alert('Erreur lors du calcul : ' + err.message);
        }
    };

    const handleKeyDown = (e) => {
        if (!ALLOWED_KEYS.includes(e.key)) e.preventDefault();
    };

    const inputClassName = 'field-facture form-control ' +
        (hasValue ? 'is-valid ' : (isFieldTouched && displayError ? 'is-invalid ' : '')) +
        (isValid === '1' ? 'success-input' : isValid === '0' ? 'error-input' : 'no-input');

    return (
        <div className="sinistre-vetuste">
            <button
                type="button"
                onClick={calculVetuste}
                onMouseEnter={handleInteraction}
                onFocus={handleInteraction}
                className={'btn btn-primary mt-2 mb-3' + (isFieldTouched && displayError ? ' border-danger' : '')}
                disabled={!canCalculate}
            >
                {hasValue ? 'Recalculer la vétusté' : 'Calculer la vétusté'}
            </button>

            {!hasValue && isFieldTouched && displayError && (
                <div className="invalid-feedback d-block mt-0 mb-4">{displayError}</div>
            )}

            <div className="form-floating">
                <input
                    id={`floating${label}`}
                    type="number"
                    name={name}
                    value={value || ''}
                    required={required}
                    step="0.01"
                    min="0"
                    className={inputClassName}
                    placeholder={placeholder}
                    onKeyDown={handleKeyDown}
                    onPaste={preventDefault}
                    onDrop={preventDefault}
                    onChange={preventDefault}
                    onWheel={blurTarget}
                    onFocus={handleInteraction}
                    onBlur={handleBlur}
                    disabled={disabled}
                    readOnly
                    {...other}
                />

                {showLabel && displayLabel && (
                    <label htmlFor={`floating${label}`}>
                        {additional_label && <b>{additional_label}</b>} {displayLabel}
                        {required && !hasValue && <span className="text-danger"> *</span>}
                    </label>
                )}

                {hasValue && isFieldTouched && displayError && (
                    <div className="invalid-feedback d-block">{displayError}</div>
                )}
            </div>

            {hasValue && vetusteResult?.explication && (
                <div className="mt-2 ml-2">
                    <p className="mb-1">Durée de vétusté : <b>{vetusteResult.palier}</b></p>
                    <p className="mb-1">Explication : <b>{vetusteResult.explication}</b></p>
                </div>
            )}
        </div>
    );
};

export default SinistreVetuste;